import pb, { requireAuth } from '../pocketbaseClient';

class ImageUploadService {
    // Upload image to PocketBase
    async uploadImage(file, metadata = {}) {
        try {
            requireAuth();
            // Create FormData for file upload
            const formData = new FormData();
            formData.append('image', file);
            
            // Add metadata if provided
            if (metadata.title) {
                formData.append('title', metadata.title);
            }
            if (metadata.description) {
                formData.append('description', metadata.description);
            }
            if (metadata.altText) {
                formData.append('alt_text', metadata.altText);
            }
            if (metadata.tags) {
                formData.append('tags', metadata.tags);
            }
            
            // Upload to PocketBase
            const record = await pb.collection('landing_page_images').create(formData);
            
            // Get the file URL using public API
            const fileUrl = `${pb.baseUrl}/api/files/${record.collectionId}/${record.id}/${record.image}`;
            
            return {
                id: record.id,
                url: fileUrl,
                filename: record.image || file.name,
                title: record.title || file.name,
                description: record.description || '',
                altText: record.alt_text || '',
                tags: record.tags || '',
                uploadedAt: record.created
            };
        } catch (error) {
            console.error('Error uploading image:', error);
            throw new Error(`Failed to upload image: ${error.message}`);
        }
    }

    // Upload questionnaire image to PocketBase
    async uploadQuestionnaireImage(file, metadata = {}) {
        try {
            requireAuth();
            // Create FormData for file upload
            const formData = new FormData();
            formData.append('image', file);
            
            // Add metadata if provided
            if (metadata.title) {
                formData.append('title', metadata.title);
            }
            if (metadata.description) {
                formData.append('description', metadata.description);
            }
            if (metadata.questionnaire_id) {
                formData.append('questionnaire_id', metadata.questionnaire_id);
            }
            if (metadata.question_id) {
                formData.append('question_id', metadata.question_id);
            }
            
            // Upload to PocketBase
            const record = await pb.collection('questionnaire_images').create(formData);
            
            // Get the file URL using public API
            const fileUrl = `${pb.baseUrl}/api/files/${record.collectionId}/${record.id}/${record.image}`;
            
            return {
                id: record.id,
                url: fileUrl,
                filename: record.image || file.name,
                title: record.title || file.name,
                description: record.description || '',
                questionnaire_id: record.questionnaire_id || '',
                question_id: record.question_id || '',
                uploadedAt: record.created
            };
        } catch (error) {
            console.error('Error uploading questionnaire image:', error);
            throw new Error(`Failed to upload questionnaire image: ${error.message}`);
        }
    }

    // Get all uploaded images
    async getImages() {
        try {
            requireAuth();
            const records = await pb.collection('landing_page_images').getFullList({
                sort: '-created'
            });
            
            return records.map(record => ({
                id: record.id,
                url: `${pb.baseUrl}/api/files/${record.collectionId}/${record.id}/${record.image}`,
                filename: record.image,
                title: record.title || '',
                description: record.description || '',
                altText: record.alt_text || '',
                tags: record.tags || '',
                uploadedAt: record.created
            }));
        } catch (error) {
            console.error('Error fetching images:', error);
            throw new Error(`Failed to fetch images: ${error.message}`);
        }
    }

    // Delete an image
    async deleteImage(imageId) {
        try {
            requireAuth();
            await pb.collection('landing_page_images').delete(imageId);
            return true;
        } catch (error) {
            console.error('Error deleting image:', error);
            throw new Error(`Failed to delete image: ${error.message}`);
        }
    }

    // Delete a questionnaire image
    async deleteQuestionnaireImage(imageId) {
        try {
            requireAuth();
            await pb.collection('questionnaire_images').delete(imageId);
            return true;
        } catch (error) {
            console.error('Error deleting questionnaire image:', error);
            throw new Error(`Failed to delete questionnaire image: ${error.message}`);
        }
    }

    // Update image metadata
    async updateImageMetadata(imageId, metadata) {
        try {
            requireAuth();
            const data = {};
            if (metadata.title !== undefined) data.title = metadata.title;
            if (metadata.description !== undefined) data.description = metadata.description;
            if (metadata.altText !== undefined) data.alt_text = metadata.altText;
            if (metadata.tags !== undefined) data.tags = metadata.tags;
            
            const record = await pb.collection('landing_page_images').update(imageId, data);
            
            return {
                id: record.id,
                url: `${pb.baseUrl}/api/files/${record.collectionId}/${record.id}/${record.image}`,
                filename: record.image,
                title: record.title || '',
                description: record.description || '',
                altText: record.alt_text || '',
                tags: record.tags || '',
                uploadedAt: record.created
            };
        } catch (error) {
            console.error('Error updating image metadata:', error);
            throw new Error(`Failed to update image: ${error.message}`);
        }
    }
}

const imageUploadService = new ImageUploadService();
export default imageUploadService;
